import { Component, computed, effect, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { AppMenuitem } from './app.menuitem';
import { PrivilegeService } from '@/services/privilege.service';
import { ClientService } from '@/services/client.service';
import { CustomerService } from '@/services/customer.service';
import { AppliactionService } from '@/services/appliaction.servicee';
import { LoginService } from '@/services/login.service';

@Component({
    selector: '[app-menu]',
    standalone: true,
    imports: [CommonModule, AppMenuitem, RouterModule],
    template: `
        <ul class="layout-menu">
            <ng-container *ngFor="let item of model; let i = index">
                <li app-menuitem *ngIf="!item.separator" [item]="item" [index]="i" [root]="true"></li>
                <li *ngIf="item.separator" class="menu-separator"></li>
            </ng-container>
        </ul>
    `
})
export class AppMenu implements OnInit {
    model: any[] = [];

    userInfo: any = null;
    isCozentusUser = false;
    isClientUser = false;
    isSuperUser = false;

    // Signal for clientId (must be read via this.clientId())
    clientId = computed(() => this.clientService.selectedClientId());

    // appId = computed(() => this.customerService.selectedIds().appId);
    // custId = computed(() => this.customerService.selectedIds().custId);

    customerId = computed(() => this.clientService.selectedCustomerId());
    applicationId = computed(() => this.applicationService.selectedAppId());
    constructor(
        private clientService: ClientService,
        private applicationService: AppliactionService,
        public privilegeService: PrivilegeService,
        private customerService: CustomerService,
        private loginService: LoginService,
        private router: Router
    ) {
        // Rebuild menu whenever clientId signal changes
        effect(() => {
            const cid = this.clientId();
            const appId = this.applicationId();
            const custId = this.customerId();

            this.buildMenu();
        });
    }

    ngOnInit(): void {
        this.userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');

        this.isCozentusUser = this.userInfo.user_type === 'COZENTUS_USER';
        this.isClientUser = this.userInfo.user_type === 'CLIENT_USER';
        this.isSuperUser = this.userInfo.is_superuser
        
        // Cozentus user resets assigned client
        // if (this.isCozentusUser) {
        //     this.clientService.clearClientId();
        // }

        this.buildMenu();
    }

    //-----------------------------------------------
    // MAIN MENU BUILDER
    //-----------------------------------------------
    buildMenu() {
        const onIdpApplication = this.router?.url?.includes('/idp-application');
        let clientId = this.clientId();
        const isSuperUser = this.loginService.isSuperUser();

        const baseMenu: any[] = [

            {
                label: ' Admin Dashboard',
                icon: 'pi pi-objects-column',
                class: 'clashy-menu-item gradient-icon-fire',
                routerLink: '/extraction/dashboard/' + (clientId || '0'),
                activeRoutes: ['dashboard'],
                visible: clientId && this.shouldShowMenuItem(['VIEW_DOCUMENT_ADMIN_DASHBOARD'])
            },
            
            {
                label: 'Dashboard',
                icon: 'pi pi-objects-column',
                class: 'clashy-menu-item gradient-icon-fire',
                routerLink: '/extraction/opex-dashboard/' + (clientId || '0'),
                activeRoutes: ['opex-dashboard'],
                visible: clientId && this.shouldShowMenuItem(['VIEW_DOCUMENT_DASHBOARD'])
            },
            {
                label: clientId ? 'File List' : 'Home',
                icon: clientId ? 'pi pi-file-export' : 'pi pi-home',
                class: 'clashy-menu-item gradient-icon-fire',
                routerLink: onIdpApplication ? null : this.getHomeRoute(),
                activeRoutes: ['idp-doc-grid', 'idp-extraction']
            },
            {
                label: 'Comparison',
                icon: 'pi pi-arrow-right-arrow-left',
                class: 'clashy-menu-item gradient-icon-star',
                routerLink: null,
                items: this.buildComparisonItems(),
                visible: clientId && this.shouldShowMenuItem(['VIEW_COMPARISON', 'VIEW_DOCUMENT_COMPARISON_BATCH'])
            },
            {
                label: 'Administration',
                icon: 'fa-solid fa-users',
                class: 'clashy-menu-item gradient-icon-star',
                routerLink: null,
                items: this.buildAdministrationItems(),
                visible: this.shouldShowMenuItem([
                    'VIEW_USER',
                    'VIEW_ROLE',
                    'VIEW_COLUMN_SETTING',
                    'VIEW_CONNECTIVITY',
                    'VIEW_USER_ACTIVITY_LOGS',
                    'VIEW_DATA_CHANGE_LOGGER_LIST',
                    'VIEW_DOCUMENT_CONFIG',
                    'VIEW_CUSTOMER_PROCESS',
                    'VIEW_BRE',
                    'VIEW_EMAIL_REFERENCE',
                    'VIEW_ETRACKER',
                    'VIEW_ANNOUNCEMENT',
                    'VIEW_USER_MANUAL'
                ])
            }
        ];

        const master = this.buildMasterDataMenu();
        if (master) baseMenu.push(master);

        baseMenu.push({
            label: 'Service Desk',
            icon: 'pi pi-comments',
            class: 'clashy-menu-item gradient-icon-tools',
            url: 'https://csm3.serviceaide.com/',
            // url: 'https://support.cozentus.com/#/login',
            target: '_blank'
        });

        this.model = baseMenu;

        
    }

    buildComparisonItems(): any[] {
        const clientId = this.clientId();

        const items = [
            {
                label: 'Comparison Configuration',
                icon: 'pi pi-arrow-right-arrow-left',
                class: 'clashy-menu-item gradient-icon-fire',
                routerLink: '/home/comparison/comparison-configuration/' + (clientId || '0'),
                activeRoutes: ['comparison-configuration'],
                visible: clientId && this.shouldShowMenuItem(['VIEW_COMPARISON'])
            },
            {
                label: 'Comparison List',
                icon: 'pi pi-wrench',
                class: 'clashy-menu-item gradient-icon-fire',
                routerLink: '/home/comparison/comparison-list/' + (clientId || '0'),
                activeRoutes: ['comparison-list'],
                visible: clientId && this.shouldShowMenuItem(['VIEW_DOCUMENT_COMPARISON_BATCH'])
            },
        ]

        return items;
    }

    //-----------------------------------------------
    // ADMINISTRATION SECTION
    //-----------------------------------------------
    buildAdministrationItems(): any[] {
        const cid = this.clientId();
        // const appId = this.appId();
        // const custId = this.custId();
        const cud = this.customerId();
        const appId = this.applicationId();

        const suffix = cid ? `/${cid}` : `/0`;

        const docConfigRoute = cud ? `${suffix}${appId ? `/${appId}` : ''}/${cud}` : `${suffix}${appId ? `/${appId}` : ''}`;

        const items = [
            {
                label: 'User Management',
                icon: 'fa-solid fa-user-gear',
                routerLink: [`/home/administration/user-management${suffix}`],
                class: 'clashy-submenu-item blue-glow',
                visible: this.shouldShowMenuItem(['VIEW_USER'])
            },
            {
                label: 'Role Management',
                icon: 'fa-solid fa-user-check',
                routerLink: [`/home/administration/role-management${suffix}`],
                class: 'clashy-submenu-item orange-glow',
                visible: this.shouldShowMenuItem(['VIEW_ROLE'])
            },
            {
                label: 'Column Settings',
                icon: 'pi pi-sliders-v',
                routerLink: [`/home/administration/column-settings${suffix}`],
                class: 'clashy-submenu-item pink-glow',
                visible: this.shouldShowMenuItem(['VIEW_COLUMN_SETTING'])
            },

            {
                label: 'Document Field Configuration',
                icon: 'pi pi-fw pi-cog',
                routerLink: [`/home/administration/document-configuration`],
                class: 'clashy-submenu-item blue-glow',
                visible: !!cid && this.shouldShowMenuItem(['VIEW_DOCUMENT_CONFIG'])
            },
            {
                label: 'BRE Configuration',
                icon: 'pi pi-fw pi-sitemap',
                routerLink: [`/home/administration/bre-configuration${suffix}`],
                class: 'clashy-submenu-item purple-glow',
                visible: !!cid && this.shouldShowMenuItem(['VIEW_BRE'])
            },
            // {
            //     label: 'Email Reference Management',
            //     icon: 'pi pi-fw pi-envelope',
            //     routerLink: [`/home/administration/email-reference-context`],
            //     class: 'clashy-submenu-item teal-glow',
            //     visible: !!cid && this.shouldShowMenuItem(['VIEW_EMAIL_REFERENCE'])
            // },
            {
                label: 'User Activity Logger',
                icon: 'fa-solid fa-user-clock',
                routerLink: [`/home/administration/activity-logger${suffix}`],
                class: 'clashy-submenu-item purple-glow',
                visible: this.shouldShowMenuItem(['VIEW_USER_ACTIVITY_LOGS'])
            },
            {
                label: 'Data Change Logger',
                icon: 'pi pi-fw pi-history',
                routerLink: [`/home/administration/data-change-log${suffix}`],
                class: 'clashy-submenu-item red-glow',
                visible: this.shouldShowMenuItem(['VIEW_DATA_CHANGE_LOGGER_LIST'])
            },
            {
                label: 'E-Tracker',
                icon: 'fa-solid fa-envelopes-bulk',
                routerLink: [`/home/administration/e-tracker${suffix}`],
                class: 'clashy-submenu-item red-glow',
                visible: !!cid && this.shouldShowMenuItem(['VIEW_ETRACKER'])
            },
            {
                label: 'Announcement Management',
                icon: 'fa-solid fa-bullhorn',
                routerLink: [`/home/administration/announcements${suffix}`],
                class: 'clashy-submenu-item red-glow',
                visible: this.shouldShowMenuItem(['VIEW_ANNOUNCEMENT'])
            }
        ];

        // Add Connectivity only when client is selected
        if (cid) {
            items.push(
                {
                    label: 'User Manual Management',
                    icon: 'fa-solid fa-book',
                    routerLink: [`/home/administration/user-manual${suffix}`],
                    class: 'clashy-submenu-item teal-glow',
                    visible: this.shouldShowMenuItem(['VIEW_USER_MANUAL'])
                },
                {
                    label: 'Connectivity Management',
                    icon: 'pi pi-fw pi-wifi',
                    routerLink: [`/home/administration/connectivity-management/${cid}`],
                    class: 'clashy-submenu-item orange-glow',
                    visible: this.shouldShowMenuItem(['VIEW_CONNECTIVITY'])
                }
            );
        }

        return items;
    }

    //-----------------------------------------------
    // MASTER DATA SECTION
    //-----------------------------------------------
    buildMasterDataMenu(): any {
        const cid = this.clientId();

        // const appId = this.appId();
        // const custId = this.custId();
        const cud = this.customerId();
        const appId = this.applicationId();
        const hasClient = !!cid;

        const items: any[] = [];

        //-----------------------------------
        // COZENTUS USER
        //-----------------------------------
        if (this.isCozentusUser) {
            if (!hasClient) {
                items.push({
                    label: 'Client Management',
                    icon: 'pi pi-fw pi-users',
                    routerLink: ['/home/masterdata/client-management/0'],
                    class: 'clashy-submenu-item violet-glow',
                    visible: true
                });
            } else if (hasClient && cid !== '0') {
                items.push(
                    {
                        label: 'Application Management',
                        icon: 'pi pi-fw pi-th-large',
                        routerLink: [`/home/masterdata/application-management/${cid}`],
                        class: 'clashy-submenu-item amber-glow',
                        visible: true
                    },
                      {
                        label: 'Process Management',
                        icon: 'pi pi-fw pi-sitemap',
                        routerLink: [`/home/masterdata/process-master-data/${cid}`],
                        class: 'clashy-submenu-item blue-glow',
                        visible: true
                    },
                    {
                        label: 'Customer Management',
                        icon: 'pi pi-fw pi-address-book',
                        routerLink: [`/home/masterdata/customer-management/${cid}`],
                        class: 'clashy-submenu-item amber-glow',
                        visible: true
                    },
                    {
                        label: 'Customer Document Management',
                        icon: 'pi pi-fw pi-file',
                        routerLink: [`/home/masterdata/document-management/${cid}`],
                        class: 'clashy-submenu-item indigo-glow',
                        visible: true
                    },
                    {
                        label: 'Document Action Management',
                        icon: 'pi pi-fw pi-file-plus',
                        routerLink: [`/home/masterdata/document-action-management/${cid}`],
                        class: 'clashy-submenu-item indigo-glow',
                        visible: true
                    },
                    {
                        label: 'Document File Type Management',
                        icon: 'pi pi-fw pi-file-edit',
                        routerLink: [`/home/masterdata/document-file-type-management/${cid}`],
                        class: 'clashy-submenu-item indigo-glow',
                        visible: true
                    },
                    {
                        label: 'Error Code Management',
                        icon: 'pi pi-fw pi-exclamation-triangle',
                        routerLink: [`/home/masterdata/error-code-management/${cid}`],
                        class: 'clashy-submenu-item red-glow',
                        visible: true
                    },
                    {
                        label: 'Extra Field Management',
                        icon: 'pi pi-fw pi-list',
                        routerLink: [`/home/masterdata/extra-field-management/${cid}`],
                        class: 'clashy-submenu-item indigo-glow',
                        visible: true
                    },
                    {
                        label: 'Code List Management',
                        icon: 'pi pi-fw pi-code',
                        routerLink: [`/home/masterdata/code-management/${cid}`],
                        class: 'clashy-submenu-item green-glow',
                        visible: true
                    },
                     {
                        label: 'Customer CONO Management',
                        icon: 'fa-regular fa-address-card',
                        routerLink: [`/home/masterdata/customer-cono-master-management`],
                        class: 'clashy-submenu-item green-glow',
                        visible: true
                    },
                    {
                        label: 'Status Category Management',
                        icon: 'fa-solid fa-layer-group',
                        routerLink: [`/home/masterdata/status-category/${cid}`],
                        class: 'clashy-submenu-item green-glow',
                        visible: true
                    }
                  
                );

            }

            return {
                label: 'Master Data',
                icon: 'pi pi-list',
                class: 'clashy-menu-item gradient-icon-palette',
                routerLink: null,
                items
            };
        }

        if (this.isClientUser) {
            items.push(
                {
                    label: 'Application Management',
                    icon: 'pi pi-fw pi-th-large',
                    routerLink: [`/home/masterdata/application-management/${cid}`],
                    class: 'clashy-submenu-item amber-glow',
                    visible: this.shouldShowMenuItem(['VIEW_APPLICATION'])
                },
                {
                    label: 'Process Management',
                    icon: 'pi pi-fw pi-sitemap',
                    routerLink: [`/home/masterdata/process-master-data/${cid}`],
                    class: 'clashy-submenu-item blue-glow',
                    visible: this.shouldShowMenuItem(['VIEW_PROCESS_MASTER'])
                },
                {
                    label: 'Customer Management',
                    icon: 'pi pi-fw pi-address-book',
                    routerLink: [`/home/masterdata/customer-management/${cid}`],
                    class: 'clashy-submenu-item amber-glow',
                    visible: this.shouldShowMenuItem(['VIEW_CUSTOMER'])
                },
                {
                    label: 'Customer Document Management',
                    icon: 'pi pi-fw pi-file',
                    routerLink: [`/home/masterdata/document-management/${cid}`],
                    class: 'clashy-submenu-item indigo-glow',
                    visible: this.shouldShowMenuItem(['VIEW_CUSTOMER_APP_DOCUMENT_TYPE_LIST', 'VIEW_CUSTOMER'])
                },
                {
                    label: 'Document Action Management',
                    icon: 'pi pi-fw pi-file-plus',
                    routerLink: [`/home/masterdata/document-action-management/${cid}`],
                    class: 'clashy-submenu-item indigo-glow',
                    visible: this.shouldShowMenuItem(['VIEW_DOCUMENT_ACTION'])
                },
                {
                    label: 'Document File Type Management',
                    icon: 'pi pi-fw pi-file-edit',
                    routerLink: [`/home/masterdata/document-file-type-management/${cid}`],
                    class: 'clashy-submenu-item indigo-glow',
                    visible: this.shouldShowMenuItem(['VIEW_DOCUMENT_FILE_TYPE'])
                },
                {
                    label: 'Error Code Management',
                    icon: 'pi pi-fw pi-exclamation-triangle',
                    routerLink: [`/home/masterdata/error-code-management/${cid}`],
                    class: 'clashy-submenu-item red-glow',
                    visible: this.shouldShowMenuItem(['VIEW_ERROR_CODE'])
                },
                {
                    label: 'Extra Field Management',
                    icon: 'pi pi-fw pi-list',
                    routerLink: [`/home/masterdata/extra-field-management/${cid}`],
                    class: 'clashy-submenu-item amber-glow',
                    visible: this.shouldShowMenuItem(['VIEW_EXTRA_CUSTOMER_FIELD'])
                },
                {
                    label: 'Code List Management',
                    icon: 'pi pi-fw pi-code',
                    routerLink: [`/home/masterdata/code-management/${cid}`],
                    class: 'clashy-submenu-item green-glow',
                    visible: this.shouldShowMenuItem(['VIEW_CODELIST'])
                },

                {
                    label: 'Customer CONO Management',
                    icon: 'fa-regular fa-address-card',
                    routerLink: [`/home/masterdata/customer-cono-master-management`],
                    class: 'clashy-submenu-item green-glow',
                    visible: this.shouldShowMenuItem(['VIEW_CUSTOMER_CONO_MASTER'])
                }
            );

            return {
                label: 'Master Data',
                icon: 'pi pi-list',
                class: 'clashy-menu-item gradient-icon-palette',
                routerLink: null,
                visible: this.shouldShowMenuItem([
                    'VIEW_APPLICATION',
                    'VIEW_CUSTOMER',
                    'VIEW_DOCUMENT_ACTION',
                    'VIEW_DOCUMENT_FILE_TYPE',
                    'VIEW_ERROR_CODE',
                    'VIEW_CODELIST',
                    'VIEW_CUSTOMER_APP_DOCUMENT_TYPE_LIST',
                    'VIEW_CUSTOMER',
                    'VIEW_EXTRA_CUSTOMER_FIELD',
                    'VIEW_CUSTOMER_CONO_MASTER',
                    'VIEW_PROCESS_MASTER'
                ]),
                items
            };
        }

        return null;
    }

    shouldShowMenuItem(requiredPrivileges: string[]): boolean {
        if (this.isSuperUser) return true;
        else if (!this.isSuperUser) return this.privilegeService.hasAnyPrivilege(requiredPrivileges);
        return false;
    }

    shouldShowConnectivityManagement(): boolean {
        if (this.isCozentusUser) return this.clientId() !== null;
        if (this.isClientUser) return this.privilegeService.hasAnyPrivilege(['VIEW_CONNECTIVITY']);
        return false;
    }

    getHomeRoute(): string[] {
        const cid = this.clientId();
        if (cid && this.isClientUser) return ['/home/extraction/idp-doc-grid',cid]
        return ['/home'];
    }

    private getStoredApplicationId(): string | null {
        return localStorage.getItem('selectedApplicationId');
    }
}
